import { t } from "@lingui/macro"
import { GridRenderCellParams } from "@mui/x-data-grid-pro"
import { useCallback, useMemo } from "react"

import { IAsyncRequestListItem } from "src/views/async-requests/store"

import { NoWrap } from "src/components/NoWrap"
import { StatusChip } from "src/components/StatusChip"
import { TruncatedText } from "src/components/TruncatedText"
import { Time } from "src/components/Time"
import { IColumn } from "src/types/data-grid-pro"

export const useGetAsyncRequestsColumns =
    (): IColumn<IAsyncRequestListItem>[] => {
        const renderRequestIdCell = useCallback(
            (params: GridRenderCellParams) => (
                <NoWrap title={params.row.request_id}>
                    <TruncatedText text={params.row.request_id} max={20} />
                </NoWrap>
            ),
            [],
        )

        const renderStatusCell = useCallback(
            (params: GridRenderCellParams) => (
                <StatusChip
                    label={params.row.status}
                    state={getStatusChipState(params.row.status)}
                />
            ),
            [],
        )

        const renderEndpointCell = useCallback(
            (params: GridRenderCellParams) => (
                <NoWrap title={params.row.endpoint}>
                    <TruncatedText text={params.row.endpoint} max={30} />
                </NoWrap>
            ),
            [],
        )

        const renderMethodCell = useCallback(
            (params: GridRenderCellParams) => (
                <NoWrap>{params.row.method}</NoWrap>
            ),
            [],
        )

        const renderProgressCell = useCallback(
            (params: GridRenderCellParams) => (
                <NoWrap>
                    {params.row.completed_items}/{params.row.total_items}
                    {params.row.failed_items > 0 &&
                        ` (${
                            params.row.failed_items
                        } ${t`async-requests.progress.failed`})`}
                </NoWrap>
            ),
            [],
        )

        const renderDateCell = useCallback(
            (params: GridRenderCellParams) =>
                params.value != null ? (
                    <NoWrap>
                        <Time date={params.value} />
                    </NoWrap>
                ) : (
                    "-"
                ),
            [],
        )

        const statusOptions = useMemo(
            () => [
                { value: "created", label: t`async-requests.status.created` },
                { value: "pending", label: t`async-requests.status.pending` },
                {
                    value: "pending_send",
                    label: t`async-requests.status.pending-send`,
                },
                { value: "sent", label: t`async-requests.status.sent` },
                {
                    value: "processing",
                    label: t`async-requests.status.processing`,
                },
                {
                    value: "completed",
                    label: t`async-requests.status.completed`,
                },
                { value: "failed", label: t`async-requests.status.failed` },
                { value: "partial", label: t`async-requests.status.partial` },
            ],
            [],
        )

        const methodOptions = useMemo(
            () => [
                { value: "GET", label: "GET" },
                { value: "POST", label: "POST" },
                { value: "PUT", label: "PUT" },
                { value: "PATCH", label: "PATCH" },
                { value: "DELETE", label: "DELETE" },
            ],
            [],
        )

        return useMemo<IColumn<IAsyncRequestListItem>[]>(
            () => [
                {
                    field: "id",
                    headerName: t`async-requests.id`,
                    width: 100,
                    type: "number",
                    filterable: true,
                    sortable: true,
                },
                {
                    field: "request_id",
                    headerName: t`async-requests.request-id`,
                    flex: 1,
                    renderCell: renderRequestIdCell,
                    type: "string",
                },
                {
                    field: "status",
                    headerName: t`async-requests.status`,
                    width: 150,
                    renderCell: renderStatusCell,
                    type: "singleSelect",
                    // @ts-ignore
                    valueOptions: statusOptions,
                },
                {
                    field: "endpoint",
                    headerName: t`async-requests.endpoint`,
                    flex: 1,
                    renderCell: renderEndpointCell,
                    type: "string",
                },
                {
                    field: "method",
                    headerName: t`async-requests.method`,
                    width: 100,
                    renderCell: renderMethodCell,
                    type: "singleSelect",
                    // @ts-ignore
                    valueOptions: methodOptions,
                },
                {
                    field: "total_items",
                    headerName: t`async-requests.progress`,
                    width: 150,
                    renderCell: renderProgressCell,
                    filterable: false,
                    sortable: false,
                },
                {
                    field: "created_at",
                    headerName: t`async-requests.created-at`,
                    width: 180,
                    renderCell: renderDateCell,
                    type: "dateTime",
                },
                {
                    field: "completed_at",
                    headerName: t`async-requests.completed-at`,
                    width: 180,
                    renderCell: renderDateCell,
                    type: "dateTime",
                },
            ],
            [
                renderRequestIdCell,
                renderStatusCell,
                renderEndpointCell,
                renderMethodCell,
                renderProgressCell,
                renderDateCell,
                statusOptions,
                methodOptions,
            ],
        )
    }

// Helper function for status chip state
function getStatusChipState(
    status: string,
): "positive" | "neutral" | "negative" | "info" | "other" | "dark" {
    switch (status) {
        case "completed":
            return "positive"
        case "failed":
            return "negative"
        case "processing":
            return "neutral"
        case "partial":
            return "neutral"
        case "pending":
        case "pending_send":
        case "sent":
            return "info"
        default:
            return "other"
    }
}
